import React from 'react';
import { FiZap } from 'react-icons/fi';
import { useAgentCosts } from '../../hooks/useAgentAnalytics';
import type { AnalyticsDateRange, AnalyticsPeriod } from '../../services/agentAnalyticsApi';

interface CostsCardProps {
  agentId: string;
  period: AnalyticsPeriod;
  /** Intervalo personalizado — substitui a janela do `period` */
  range?: AnalyticsDateRange;
}

function formatCredits(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return value.toLocaleString('pt-BR');
}

export const CostsCard: React.FC<CostsCardProps> = ({ agentId, period, range }) => {
  const { data: costs, isLoading } = useAgentCosts(agentId, period, range);

  const totalCredits = costs?.totalCredits ?? 0;
  const totalInputCredits = costs?.totalInputCredits ?? 0;
  const totalOutputCredits = costs?.totalOutputCredits ?? 0;

  const totalExecs = costs?.byDay.reduce((sum, d) => sum + d.executions, 0) ?? 0;
  const avgPerExec = totalExecs > 0 ? Math.round(totalCredits / totalExecs) : 0;

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-sm font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Créditos
        </h3>
        <FiZap
          className="h-5 w-5"
          style={{ color: 'var(--theme-primary)' }}
        />
      </div>

      {isLoading && (
        <div
          className="text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Carregando...
        </div>
      )}

      {!isLoading && costs && (
        <>
          <div
            className="text-3xl font-bold mb-1"
            style={{ color: 'var(--theme-card-text)' }}
          >
            {formatCredits(totalCredits)}
          </div>
          <div
            className="text-xs mb-3"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            créditos consumidos no período
          </div>

          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--theme-muted-text)' }}>
                Entrada / Saída
              </span>
              <span
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {formatCredits(totalInputCredits)} / {formatCredits(totalOutputCredits)}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--theme-muted-text)' }}>
                Média por execução
              </span>
              <span
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {formatCredits(avgPerExec)}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--theme-muted-text)' }}>
                Total de execuções
              </span>
              <span
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {totalExecs}
              </span>
            </div>
          </div>

          {costs.byModel.length > 0 && (
            <div
              className="mt-3 pt-3 border-t"
              style={{ borderColor: 'var(--theme-card-border)' }}
            >
              <div
                className="text-xs font-medium mb-1"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Por modelo
              </div>
              {costs.byModel.slice(0, 3).map((m) => (
                <div
                  key={m.model}
                  className="flex justify-between text-xs py-0.5"
                >
                  <span style={{ color: 'var(--theme-muted-text)' }}>
                    {m.model}
                  </span>
                  <span style={{ color: 'var(--theme-card-text)' }}>
                    {formatCredits(m.inputCredits + m.outputCredits)} cr
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
